import { ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router';

export interface BreadcrumbMatch {
  pathname: string;
  breadcrumb: ReactNode;
}

// Helper function to flatten nested routes
const flattenRoutes = (routes: any[], parentPath = ''): any[] => {
  const flattened: any[] = [];

  routes.forEach((route) => {
    const fullPath = parentPath + (route.path || '');

    // Add the current route if it has a breadcrumb
    if (route.breadcrumb !== undefined) {
      flattened.push({
        ...route,
        fullPath: fullPath || parentPath,
      });
    }

    // Recursively flatten children
    if (route.children) {
      flattened.push(...flattenRoutes(route.children, fullPath));
    }
  });

  return flattened;
};

// Helper function to match route patterns with the current pathname
const matchRoute = (routePath: string, pathname: string): boolean => {
  if (routePath === pathname) return true;

  // Handle dynamic routes (e.g., /user/:id)
  const routePattern = routePath.replace(/:\w+/g, '[^/]+');
  const regex = new RegExp(`^${routePattern}$`);
  return regex.test(pathname);
};

// Helper function to build breadcrumb path segments
const buildPathSegments = (pathname: string): string[] => {
  const segments = pathname.split('/').filter(Boolean);
  const paths: string[] = [];

  for (let i = 0; i < segments.length; i++) {
    const path = '/' + segments.slice(0, i + 1).join('/');
    paths.push(path);
  }

  return paths;
};

export const useBreadcrumbs = (
  routes: any[],
  options: { excludePaths?: string[] } = {},
): BreadcrumbMatch[] => {
  const location = useLocation();
  const { excludePaths = [] } = options;

  return useMemo(() => {
    const breadcrumbs: BreadcrumbMatch[] = [];
    const { pathname } = location;

    // Skip excluded paths
    if (excludePaths.includes(pathname)) {
      return breadcrumbs;
    }

    // Flatten all routes for easier matching
    const flatRoutes = flattenRoutes(routes);

    // Build all possible path segments from the current pathname
    const pathSegments = buildPathSegments(pathname);

    // For each path segment, try to find a matching route with breadcrumb
    pathSegments.forEach((segmentPath) => {
      if (excludePaths.includes(segmentPath)) {
        return;
      }

      const matchingRoute = flatRoutes.find((route) =>
        matchRoute(route.fullPath, segmentPath),
      );

      if (matchingRoute && matchingRoute.breadcrumb !== null) {
        let breadcrumbText: ReactNode = matchingRoute.breadcrumb;
        
        // If breadcrumb is a function, call it with route info
        if (typeof matchingRoute.breadcrumb === 'function') {
          breadcrumbText = matchingRoute.breadcrumb({ pathname: segmentPath });
        }

        // Default fallback if no breadcrumb defined
        if (!breadcrumbText) {
          breadcrumbText = segmentPath.split('/').pop() || 'Home';
        }

        breadcrumbs.push({
          pathname: segmentPath,
          breadcrumb: breadcrumbText,
        });
      }
    });

    return breadcrumbs;
  }, [location.pathname, routes, excludePaths]);
};
